<template>
    <div>
        <b-card
            :id="'unit_group' + unit_group.id"
            class="unit-group mb-3"
            :class="{
                'dark': dark,
                'new': unit_group.is_new,
                'deleting': unit_group.deleting,
            }"
            :header-bg-variant="dark ? 'dark' : 'primary'"
            header-text-variant="white"
            :border-variant="dark ? 'dark' : null"
            no-body
        >
            <template #header>
                <CardID :id="unit_group.id" type="Unit" variant="light" />
                <div class="d-flex align-items-center">
                    <div class="d-flex align-items-center flex-grow-1">
                        <span
                            class="collapse-icon mr-1"
                            :class="unit_group.visible ? null : 'collapsed'"
                            :aria-expanded="unit_group.visible ? 'true' : 'false'"
                            @click="toggleVisibility('visible')"
                        >
                            <span class="open">
                                <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                            </span>
                            <span class="closed">
                                <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                            </span>
                        </span>

                        <div class="mr-2">
                            <select
                                v-model="unit_group.order_number"
                                class="form-control form-control-sm custom-select custom-select-sm text-dark"
                                @change="reorderUnitGroups(unit_group)"
                            >
                                <option v-for="unit_group in unit_groups" :key="unit_group.id" :value="unit_group.order_number">
                                    Unit {{unit_group.order_number}}
                                </option>
                            </select>
                        </div>

                        <div class="flex-grow-1 mr-3">
                            <Title
                                v-model="unit_group.title"
                                :unit_group="unit_group"
                                :variant="'dark'"
                                @update="updateUnitGroupTitle"
                                :disabled="disabled"
                            />
                        </div>
                    </div>
                    <div class="ml-auto mr-3">
                        <span v-if="unit_group.deleting" class="text-white">
                            <font-awesome-icon :icon="['fad', 'circle-notch']" spin />
                        </span>
                        <span v-else>
                            <b-dropdown
                                size="sm"
                                variant="light"
                                dropleft
                            >
                                <template #button-content>
                                    <font-awesome-icon :icon="['fad', 'cog']" />
                                </template>

                                <!-- added back into trunk 4/16/2024 -->
                                <b-dropdown-item @click="startUnitGroupDuplication" :disabled="!can_duplicate_unit">
                                    <font-awesome-icon :icon="['fad', 'clone']" class="text-info" fixed-width /> Duplicate
                                </b-dropdown-item>

                                <b-dropdown-item @click="deleteUnitGroup" :disabled="!can_delete_unit || disabled">
                                    <font-awesome-icon :icon="['fad', 'trash']" class="text-danger" fixed-width /> Delete
                                </b-dropdown-item>
                                <template v-if="can_request_report">
                                    <b-dropdown-divider />
                                    <b-dropdown-header>Reports</b-dropdown-header>
                                    <b-dropdown-item @click="$emit('request-weekly-phenomenon-report', {
                                        unitGroupId: unit_group.id,
                                        label: `Unit ${unit_group.order_number}: ${unit_group.title || 'No title'}`,
                                    })">
                                        <font-awesome-icon :icon="['fad', 'file-alt']" class="text-primary" fixed-width />
                                        Weekly Phenomenon
                                    </b-dropdown-item>
                                    <b-dropdown-item @click="$emit('view-report-requests', {
                                        type: 'unit',
                                        id: unit_group.id,
                                        label: `Unit ${unit_group.order_number}: ${unit_group.title || 'No title'}`,
                                    })">
                                        <font-awesome-icon :icon="['fad', 'list-alt']" class="text-secondary" fixed-width />
                                        View Reports
                                    </b-dropdown-item>
                                </template>
                            </b-dropdown>
                        </span>
                    </div>
                </div>
                <div v-if="unit_group && unit_group.trunks && unit_group.trunks.length > 0 && publication.is_trunk != 1" class="row offset-md alert alert-info mt-2 pt-2">
                    <div class="col">
                        <div>Trunk SKU: {{ unit_group.trunks[0].sku }}</div>
                        <div>Unit Title: {{ unit_group.trunks[0].unit_group_title }}</div>
                        <div>Unit Id: {{ unit_group.trunks[0].unit_group_id }}</div>
                    </div>
                    <div class="col">
                        <button @click="compareToTrunk(unit_group)" :class="unit_group.trunk_differences ? 'btn btn-danger btn-sm float-right' : 'btn btn-primary btn-sm float-right'">View Trunk Differences</button>
                    </div>
                </div>
            </template>

            <b-card-body>
                <div v-if="isElementVisible(element_visibility, 'unit_group_standards')" :id="'unit_group' + unit_group.id + 'standards'">
                    <div class="h6">
                        Unit Standards
                        (<span
                            v-if="unit_group.filtered_standards"
                            :class="{'text-success': filtered_unit_group_standards.length, 'text-danger': !filtered_unit_group_standards.length}"
                        >{{filtered_unit_group_standards.length}}</span>)
                    </div>
                    <standards-multiselect
                        v-model="filtered_unit_group_standards"
                        :standards="filtered_available_standards"
                        :state_grade="state_grade"
                        @addStandard="addStandard"
                        @removeStandard="removeStandard"
                        :disabled="disabled"
                    />
                </div>
            </b-card-body>

            <b-collapse
                v-model="unit_group.visible"
                :class="unit_group.visible ? null : 'collapsed'"
            >
                <div v-if="unit_group.visible && unit_group_mounted">
                    <b-list-group>
                        <b-list-group-item v-if="isElementVisible(element_visibility, 'unit_group_panels')" class="panel-section-container">
                            <div class="h6">Panels</div>
                            <panel-section
                                :id="'unit_group' + unit_group.id + 'panel'"
                                :panels="unit_group.panels"
                                :unit_group="unit_group"
                                :publication="publication"
                                :disabled="disabled"
                            />
                        </b-list-group-item>

                        <!-- Trunk Pedigree -->
                        <b-list-group-item
                            v-if="publication.is_trunk == 1 && isElementVisible(element_visibility, 'unit_group_trunk_pedigree')"
                            :id="'unit_group' + unit_group.id + 'trunk-pedigree'"
                            class="text-dark"
                        >
                            <div class="h6 mb-0">
                                <span
                                    class="collapse-icon mr-1"
                                    :class="unit_group.trunk_pedigree_visible ? null : 'collapsed'"
                                    :aria-expanded="unit_group.trunk_pedigree_visible ? 'true' : 'false'"
                                    @click.prevent="toggleVisibility('trunk_pedigree_visible')"
                                >
                                    <span class="open">
                                        <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                    </span>
                                    <span class="closed">
                                        <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                    </span>
                                    Trunk Unit Pedigree
                                </span>
                            </div>

                            <b-collapse
                                :id="'trunkPedigreeCollapse' + unit_group.id"
                                v-model="unit_group.trunk_pedigree_visible"
                                :class="unit_group.trunk_pedigree_visible ? null : 'collapsed'"
                            >
                                <div v-if="unit_group.trunk_pedigree_visible" class="mt-3">
                                    <table class="table table-striped">
                                        <thead>
                                            <tr>
                                                <th class="col"> SKU </th>
                                                <th class="col"> Unit </th>
                                                <th class="col"> Title </th>
                                                <th class="col"> Unit Id </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="branch in unit_group.branches" class="">
                                                <td class=""> {{ branch.sku }} </td>
                                                <td class=""> {{ branch.unit_group_number }} </td>
                                                <td class=""> {{ branch.unit_group_title }} </td>
                                                <td class=""> {{ branch.branch_unit_group_id }} </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </b-collapse>
                        </b-list-group-item>

                        <b-list-group-item
                            v-show="workflow !== 'standards' && isElementVisible(element_visibility, 'unit_group_assessment_questions')"
                            :id="'unit_group' + unit_group.id + 'assessment'"
                            class="text-dark"
                        >
                            <div class="h6 mb-0">
                                <span
                                    class="collapse-icon mr-1"
                                    :class="unit_group.assessment_visible ? null : 'collapsed'"
                                    :aria-expanded="unit_group.assessment_visible ? 'true' : 'false'"
                                    @click="toggleVisibility('assessment_visible')"
                                >
                                    <span class="open">
                                        <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                    </span>
                                    <span class="closed">
                                        <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                    </span>
                                    Unit Assessment
                                </span>
                            </div>

                            <b-collapse
                                v-model="unit_group.assessment_visible"
                                :class="unit_group.assessment_visible ? null : 'collapsed'"
                            >
                                <div
                                    :id="'unit_group' + unit_group.id + 'assessment-questions'"
                                >
                                    <div v-if="unit_group.assessment_visible">

                                        <div class="questions">
                                            <div v-if="questions_loading">
                                                <content-placeholders>
                                                    <content-placeholders-text :lines="3" />
                                                </content-placeholders>
                                            </div>
                                            <div v-else>
                                                <div v-sticky="{zIndex: 11, stickyTop: 144}" class="d-flex align-items-center bg-white py-3">
                                                    <div class="mr-3">
                                                        Questions
                                                    </div>
                                                    <div class="ml-auto">
                                                        <span class="mr-3">
                                                            <a :href="'/questionList/unit_group/'+unit_group.id" target="_blank" class="pull-right" ><font-awesome-icon :icon="['fad', 'clipboard']" style="color: grey;" fixed-width /></a>
                                                        </span>
                                                        <b-dropdown
                                                            size="sm"
                                                            variant="outline-primary"
                                                            right
                                                            :disabled="!can_add_question || disabled"
                                                        >
                                                            <template #button-content>
                                                                <font-awesome-icon :icon="['fad', 'plus-square']" /> Add Question
                                                            </template>
                                                            <b-dropdown-item @click="newQuestion">
                                                                <font-awesome-icon :icon="['fad', 'plus-square']" class="text-success" fixed-width /> Create Question
                                                            </b-dropdown-item>
                                                        </b-dropdown>
                                                    </div>
                                                </div>

                                                <div
                                                    v-for="(question, index) in questions"
                                                    :key="index"
                                                >
                                                    <Question
                                                        :publication_id="publication_id"
                                                        :unit_group="unit_group"
                                                        :question="question"
                                                        :questions="questions"
                                                        :state_grade="state_grade"
                                                        :available_standards="filtered_unit_group_standards"
                                                        @update="getQuestions"
                                                        :disabled="disabled"
                                                    />
                                                </div>

                                                <b-alert
                                                    variant="danger"
                                                    :show="!questions || questions && !questions.length"
                                                    class="mb-2"
                                                >
                                                    0 Unit Questions
                                                </b-alert>
                                            </div>
                                        </div>


                                    </div>
                                </div>

                            </b-collapse>
                        </b-list-group-item>

                        <b-list-group-item
                            v-if="isElementVisible(element_visibility, 'unit_group_assessment_pdf')"
                            :id="'unit_group' + unit_group.id + 'unit-assessment-pdf'"
                            class="graphic-organizer"
                        >
                            <div class="mt-3">
                                <label>Unit Assessment PDF</label>
                                <div v-if="uploading_assessment">
                                    <b-spinner small label="Loading..."></b-spinner>
                                </div>
                                <div v-else>
                                    <div v-if="unit_group.assessment_url">
                                        <b-list-group>
                                            <b-list-group-item>
                                                <div class="d-flex align-items-center">
                                                    <div>
                                                        <b-link :href="`${AWS_URL}${unit_group.assessment_url}`" target="_blank" class="text-primary">
                                                            {{unit_group.assessment_title}}
                                                        </b-link>
                                                    </div>
                                                    <div class="ml-auto actions" v-if="!disabled">
                                                        <b-link
                                                            href="#"
                                                            class="text-danger"
                                                            @click.prevent="removeUnitAssessment"
                                                        >
                                                            <font-awesome-icon :icon="['fad', 'trash']" />
                                                        </b-link>
                                                    </div>
                                                </div>
                                            </b-list-group-item>
                                        </b-list-group>
                                    </div>
                                    <div v-else>
                                        <b-form-file
                                            v-model="assessment_url"
                                            accept="application/pdf"
                                            @change="uploadUnitAssessment($event)"
                                            :disabled="disabled"
                                        />
                                        <b-form-text>
                                            <span class="text-danger">No file has been uploaded</span>
                                        </b-form-text>
                                    </div>
                                </div>
                            </div>
                        </b-list-group-item>

                        <b-list-group-item
                            v-if="isElementVisible(element_visibility, 'unit_group_printables')"
                            :id="'unit_group' + unit_group.id + 'graphic-organizers'"
                            class="graphic-organizer"
                        >
                            <div class="h6 mb-0">
                                <span
                                    class="collapse-icon text-dark mr-1"
                                    :class="unit_group.worksheets_visible ? null : 'collapsed'"
                                    :aria-expanded="unit_group.worksheets_visible ? 'true' : 'false'"
                                    @click="toggleVisibility('worksheets_visible')"
                                >
                                    <span class="open">
                                        <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                    </span>
                                    <span class="closed">
                                        <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                    </span>
                                    Unit Printables
                                </span>
                            </div>

                            <b-collapse
                                v-model="unit_group.worksheets_visible"
                                :class="unit_group.worksheets_visible ? null : 'collapsed'"
                            >
                                <WorksheetsList
                                    :unit_group="unit_group"
                                    :publication="publication"
                                    :disabled="disabled"
                                />
                            </b-collapse>
                        </b-list-group-item>
                        <b-list-group-item
                            v-if="isElementVisible(element_visibility, 'unit_group_weekly_issues_list')"
                            :id="'unit_group' + unit_group.id + 'weekly-issues'"
                            class="text-dark"
                        >
                            <div class="h6 mb-0">
                                <span
                                    class="collapse-icon mr-1"
                                    :class="unit_group.units_visible ? null : 'collapsed'"
                                    :aria-expanded="unit_group.units_visible ? 'true' : 'false'"
                                    @click.prevent="toggleVisibility('units_visible')"
                                    v-if="!refreshing_unit_list"
                                >
                                    <span class="open">
                                        <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                    </span>
                                    <span class="closed">
                                        <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                    </span>
                                    <span v-if="publication.are_lessons">Lesson</span><span v-else>Weekly</span> Issues  (<span v-if="unit_group.units && unit_group.units.length" class="text-success">{{unit_group.units.length}}</span><span v-else class="text-danger">0</span>)
                                </span>
                            </div>

                            <b-collapse
                                :id="'weeksCollapse' + unit_group.id"
                                v-model="unit_group.units_visible"
                                :class="unit_group.units_visible ? null : 'collapsed'"
                            >
                                <div v-if="unit_group.units_visible && !refreshing_unit_list" class="mt-3">

                                    <div
                                        v-for="(unit, index) in sorted_units"
                                        class="unit mt-2"
                                        :class="{'draggable': !unit.visible}"
                                        :key="index"
                                    >
                                        <Unit
                                            :publication_id="publication_id"
                                            :publication="publication"
                                            :unit_groups="unit_groups"
                                            :unit_group="unit_group"
                                            :unit="unit"
                                            :units_are_lessons="units_are_lessons"
                                            :dark="true"
                                            :state_grade="state_grade"
                                            @refresh="getUnitGroups"
                                            @request-report="$emit('request-week-report', $event)"
                                            @view-report-requests="$emit('view-report-requests', $event)"
                                            :disabled="disabled"
                                        />
                                    </div>

                                    <b-alert
                                        variant="danger"
                                        :show="!unit_group.units || unit_group.units && !unit_group.units.length"
                                        class="my-2"
                                    >
                                        0 Weekly Issues
                                    </b-alert>

                                    <div class="ml-3 float-right">
                                        <b-btn
                                            size="sm"
                                            variant="info"
                                            @click="show_upload_blueprint_modal = true"
                                            :disabled="!can_upload_blueprint || disabled"
                                        >
                                            <font-awesome-icon :icon="['fad', 'plus-square']" /> Upload Weekly Blueprint
                                        </b-btn>

                                        <b-btn
                                            title="How the material quantity should be distributed among students."
                                            variant="link"
                                            class="p-0 ml-2"
                                            tabindex="-1"
                                            @click="show_blueprint_tokens_modal =! show_blueprint_tokens_modal"
                                        >
                                            <font-awesome-icon :icon="['fad', 'info-circle']"/>
                                        </b-btn>
                                    </div>
                                    
                                    <div class="ml-auto float-right">
                                        <b-btn
                                            size="sm"
                                            variant="success"
                                            @click="confirmCreateUnit(unit_group)"
                                            :disabled="!can_create_week || disabled"
                                        >
                                            <font-awesome-icon :icon="['fad', 'plus-square']" /> Create Weekly Issue
                                        </b-btn>
                                    </div>

                                    <div v-if="publication.is_trunk != 1" class="ml-auto float-right mr-3">
                                        <b-btn
                                            size="sm" variant="primary"
                                            @click="importTrunkUnit(unit_group)"
                                            :disabled="!can_import_week_from_trunk || disabled"
                                        >
                                            <font-awesome-icon :icon="['fad', 'plus-square']" /> Import From Trunk
                                        </b-btn>
                                    </div>

                                    <div v-if="publication.is_trunk == 1" class="ml-auto float-right mr-3">
                                        <b-btn size="sm" variant="primary" @click="importBranchUnit(unit_group)" :disabled="!can_import_week_from_trunk">
                                            <font-awesome-icon :icon="['fad', 'plus-square']" /> Import Existing Week To Trunk
                                        </b-btn>
                                    </div>
                                </div>
                            </b-collapse>
                        </b-list-group-item>
                    </b-list-group>
                </div>
            </b-collapse>
        </b-card>

        <trunk-import-modal
            v-model="show_trunk_import_modal"
            :publication_id="publication.id"
            :publication_is_trunk="publication.is_trunk"
            :unit_group="unit_group"
            :import_level="'unit'"
            @hide="show_trunk_import_modal = false"
            @getUnits="getUnitGroups"
        />

        <duplicate-unit-group-modal
            v-model="show_duplicate_unit_group_modal"
            @duplicate="duplicateUnitGroup"
            @hide="show_duplicate_unit_group_modal = false"
        />

        <question-modal
            v-model="show_question_modal"
            :unit_group="unit_group"
            :questions="questions"
            :state_grade="state_grade"
            :available_standards="filtered_unit_group_standards"
            @create="createQuestion"
            @hide="show_question_modal = false"
        />

        <trunk-unit-comparison-modal
            v-model="show_trunk_unit_comparison_modal"
            :comparison="trunk_unit_comparison"
            :unit_group="unit_group"
            @hide="show_trunk_unit_comparison_modal = false"
            @update_to_trunk="updateToTrunkUnit"
            @refreshUnitGroup="refreshUnitGroup"
        />

        <upload-blueprint-modal
            v-model="show_upload_blueprint_modal"
            :unit_group="unit_group"
            @hide="show_upload_blueprint_modal = false"
            @getUnits="getUnitGroups"
        />

        <blueprint-tokens-modal
            v-model="show_blueprint_tokens_modal"
            @hide="show_blueprint_tokens_modal = false"
        />


    </div>
</template>

<style lang="scss">
.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
</style>

<style lang="scss" scoped>
@import '@/assets/css/_colors';

.card {
    .card-header {
        border-top-left-radius: 0.25rem;
        border-top-right-radius: 0.25rem;
    }
}

.bg-primary {
    background-color: #376971 !important;
}

.panel-section-container {
    border-radius: unset !important;
}
</style>

<script>
import Vue from 'vue'
import VueSticky from 'vue-sticky'
import draggable from 'vuedraggable'

import { mapState } from 'vuex'
import { isElementVisible } from '@/common/elementVisibility'

import VueScrollTo from 'vue-scrollto'

import CardID from '@/components/CardID'
import Title from '@/components/Title'

import TipTap from '@/components/tiptap/TipTap'

import Unit from '@/components/unit/Index'
import Question from '@/components/unit_group/Question'

import StandardsMultiselect from '@/components/standards/StandardsMultiselect'
import DuplicateUnitGroupModal from '@/components/modals/unit_group/DuplicateUnitGroupModal'

import QuestionModal from '@/components/modals/questions/QuestionModal'
import PanelSection from '@/components/panels/PanelSection'

import TrunkImportModal from '@/components/modals/unit/TrunkImportModal'
import TrunkUnitComparisonModal from '@/components/modals/unit/TrunkUnitComparisonModal'

import UploadBlueprintModal from '@/components/modals/unit/UploadBlueprintModal'
import BlueprintTokensModal from '@/components/modals/unit/BlueprintTokensModal'

import colors from '@/common/colors'
import { AWS_URL, convertCdnUrlToAWS } from '@/common/urlHelpers'
import WorksheetsList from '@/components/worksheet/WorksheetsList'

export default {
    data: () => ({
        colors: colors,
        questions: [],
        filtered_unit_group_standards: [],
        available_standards: [],
        filtered_available_standards: [],
        unit_group_cache: {},
        editable_question: {},
        unit_group_mounted: false,
        show_duplicate_unit_group_modal: false,
        assessment_url: null,
        uploading_assessment: false,
        questions_loading: false,
        show_question_modal: false,
        show_trunk_import_modal: false,
        refreshing_unit_list: false,
        show_trunk_unit_comparison_modal: false,
        trunk_unit_comparison: [],
        publication_worksheets: null,
        colorOptions: [
            { value: null, text: 'Select Worksheet Header Color' },
            { value: 'green', text: 'Green'},
            { value: 'blue', text: 'Blue'},
            { value: 'orange', text: 'Orange'},
            { value: 'red', text: 'Red'},
        ],
        show_upload_blueprint_modal: false,
        show_blueprint_tokens_modal: false,
    }),
    props: [
        'publication_id',
        'publication',
        'unit_group',
        'unit_groups',
        'dark',
        'state_grade',
        'units_are_lessons',
        'disabled',
        'selected_standard_year',
    ],
    components: {
        WorksheetsList,
        draggable,
        CardID,
        Title,
        TipTap,
        Unit,
        Question,
        StandardsMultiselect,
        DuplicateUnitGroupModal,
        QuestionModal,
        PanelSection,
        TrunkImportModal,
        TrunkUnitComparisonModal,
        UploadBlueprintModal,
        BlueprintTokensModal,
    },
    directives: {
        'sticky': VueSticky,
    },
    computed: {
        ...mapState({
            standards: state => state.standards.standards,
            workflow: state => state.utilities.workflow,
            wysiwyg_menu_type: state => state.settings.wysiwyg_menu_type,
            element_visibility: state => state.settings.element_visibility,
            user: state => state.user.user
        }),
        sorted_units: {
            get() {
                return _.orderBy(this.unit_group.units, 'order_number')
            },
            set() {}
        },
        can_duplicate_unit() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'duplicate_unit');
        },
        can_delete_unit() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'delete_unit');
        },
        can_request_report() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'request_report')
        },
        can_add_question() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'add_question');
        },
        can_create_week() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'create_week');
        },
        can_import_week_from_trunk() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'import_trunk_week');
        },
        can_upload_blueprint() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'upload_blueprint');
        },
        AWS_URL() {
            return AWS_URL;
        }
    },
    watch: {
        'available_standards':{
            handler (visible) {
                this.filtered_available_standards = [];
                _.each(this.available_standards, stand => {
                    if(this.selected_standard_year == 'None'){
                        this.filtered_available_standards.push(stand);
                    }
                    else if(this.selected_standard_year != 'None' && stand.year == this.selected_standard_year){
                        this.filtered_available_standards.push(stand);
                    }
                })
            }
        },
        'selected_standard_year':{
            handler (visible) {
                this.filtered_available_standards = [];
                _.each(this.available_standards, stand => {
                    if(this.selected_standard_year == 'None'){
                        this.filtered_available_standards.push(stand);
                    }
                    else if(this.selected_standard_year != 'None' && stand.year == this.selected_standard_year){
                        this.filtered_available_standards.push(stand);
                    }
                })
            }
        },
        'unit_group.visible': {
            handler (visible) {
                localStorage.setItem('Publication' + this.publication_id + 'Group' + this.unit_group.id, visible)
            },
            immediate: false
        },
        'unit_group.units_visible': {
            handler (visible) {
                localStorage.setItem('Publication' + this.publication_id + 'Group' + this.unit_group.id + 'Units', visible)
            },
            immediate: false
        },
        'unit_group.assessment_visible': {
            handler (visible) {
                if (visible) {
                    this.questions_loading = true

                    this.getQuestions()
                    .then(() => {
                        this.questions_loading = false
                    })
                    .catch(error => {
                        if (error) {
                            if (
                                error.response &&
                                error.response.status &&
                                error.response.status == 401
                            ) {
                                this.$set(this.unit_group, 'assessment_visible', false)
                            }
                        }
                    })
                }
            },
            immediate: false
        },
        state_grade(state_grade) {
            this.filterUnitGroupStandards(state_grade)
        },
    },
    methods: {
        convertCdnUrlToAWS,
        isElementVisible,
        filterUnitGroupStandards(state_grade) {
            //filtered_unit_group_standardsprovides the list of standards already assigned at the unit level.
            this.filtered_unit_group_standards = []
            _.each(this.unit_group.standards, standard => {
                if(state_grade.standards_source == 'national'){
                    if(state_grade
                        && standard.state == 'NA'
                        && state_grade.grade >= standard.low_grade
                        && state_grade.grade <= standard.high_grade
                    ){
                        this.filtered_unit_group_standards.push(standard)
                    }
                }
                else if (state_grade.standards_source == 'state'){
                    if(state_grade
                        && state_grade.state == standard.state
                        && ( state_grade.grade >= standard.low_grade && state_grade.grade <= standard.high_grade )
                    ){
                        this.filtered_unit_group_standards.push(standard)
                    }
                }
            })
            this.$set(this.unit_group, 'filtered_standards', this.filtered_unit_group_standards)

            //available_standards arrary will populate the standards available for assignment in the unit standards dropdown
            this.available_standards = []
            _.each(this.standards, standard => {
                if(
                    state_grade
                    &&
                    state_grade.standards_source != 'none'
                    &&
                    (
                        (state_grade.standards_source == 'national' && standard.state == 'NA' && state_grade.grade <= standard.high_grade && state_grade.grade >= standard.low_grade)
                        ||
                        (state_grade.standards_source == 'state' && standard.state == state_grade.state && state_grade.grade <= standard.high_grade && state_grade.grade >= standard.low_grade)
                    )
                ){
                    this.available_standards.push(standard);
                }
                if(!state_grade) this.available_standards.push(standard)
            })
        },
        reorderUnitGroups(unit_group) {
            this.axios.post('/api/unit_groups/reorder/', {
                publication_id: this.publication.id,
                unit_group_id: unit_group.id,
                old_order_number: unit_group.old_order_number,
                new_order_number: unit_group.order_number
            })
            .then(() => {
                this.$store.dispatch('getUnitGroups', this.publication_id)
            })
        },
        getQuestions () {
            return new Promise((resolve, reject) => {
                this.axios.get('/api/unit_groups/questions/' + this.unit_group.id)
                .then(response => {
                    let questions = []

                    if (response.data) {
                        questions = response.data
                        questions = _.orderBy(questions, 'order_number')
                    }

                    this.questions = questions

                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
            })
        },
        updateUnitGroup () {
            this.axios.post('/api/unit_groups/update/' + this.unit_group.id, this.unit_group)
            .then(response => {
                this.unit_group_cache = _.cloneDeep(response.data)
            })
        },
        updateUnitGroupByProperty (key, value) {
            return new Promise((resolve, reject) => {
                this.$Progress.start()

                this.axios.post('/api/unit_groups/update/' + this.unit_group.id, {
                    [key]: value
                })
                .then(response => {
                    this.unit_group_cache = _.cloneDeep(response.data)

                    this.$Progress.finish()

                    resolve(response)
                }).catch(error => {
                    if (error) {
                        this.$Progress.fail()

                        _.assign(this.unit_group, this.unit_group_cache)
                    }

                    reject(error)
                })
            })
        },
        updateUnitGroupTitle (title) {
            this.updateUnitGroupByProperty('title', title)
            .then(() => {
                this.$set(this.unit_group, 'title', title)
            })
        },
        startUnitGroupDuplication () {
            this.show_duplicate_unit_group_modal = true
        },
        duplicateUnitGroup (target_publication) {
            this.axios.post('/api/unit_groups/duplicate', {
                old_publication_id: this.publication.id,
                new_publication_id: target_publication.id,
                unit_group_id: this.unit_group.id
            })
            .then(() => {
                this.$bvToast.toast('Unit duplicated', {
                    title: 'Success',
                    variant: 'success',
                    solid: false,
                    autoHideDelay: 3000,
                    appendToast: true
                })

                this.show_duplicate_unit_group_modal = false
            })
        },
        deleteUnitGroup () {
            let message = null

            if (this.unit_group.title) {
                message = 'Are you sure you want to delete the following unit? <div class="mt-3 text-left pl-3 border-left"><div>Title: <strong>' + this.unit_group.title + '</strong></div></div>'
            } else {
                message = 'Are you sure you want to delete this unit?'
            }

            this.$swal({
                html: message,
                showCancelButton: true,
                confirmButtonText: 'Delete Unit',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.$set(this.unit_group, 'deleting', true)

                    this.axios.post('/api/unit_groups/delete/' + this.unit_group.id)
                    .then(() => {
                        this.$set(this.unit_group, 'deleting', false)

                        if (localStorage.getItem('Group' + this.unit_group.id)) {
                            localStorage.removeItem('Group' + this.unit_group.id)
                        }

                        this.$store.dispatch('getUnitGroups', this.publication_id)
                    })
                    .catch(() => {
                        this.$set(this.unit_group, 'deleting', false)
                    })
                }
            })
        },
        uploadBlueprint(unit_group){
            console.log('uploadBlueprint', unit_group);
            this.show_upload_blueprint_modal = true;
        },
        confirmCreateUnit(unit_group){
            if(this.publication.is_trunk != 1){
                this.$swal({
                    html: 'This Weekly Issue will only appear in this Publication and Unit',
                    showCancelButton: true,
                    confirmButtonText: 'Create Weekly Issue',
                    confirmButtonColor: '#E67E22',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.createUnit();
                    }
                })
            }
            else{
                this.createUnit();
            }
        },
        createUnit () {
            this.refreshing_unit_list = true;
            this.axios.post('/api/units/create', {
                publication_id: this.unit_group.publication_id,
                unit_group_id: this.unit_group.id,
                order_number: this.newOrderNumber(this.unit_group.id)
            })
            .then(response => {
                let unit = response.data
                unit.standards = []
                unit.pivot = unit.pivot || { worksheet_color: null }
                this.filterUnitGroupStandards(null)

                this.unit_group.units.push(unit)

                Vue.nextTick(() => {
                    this.refreshing_unit_list = false;
                    VueScrollTo.scrollTo('#unit' + unit.id, 0, {
                        offset: -144
                    })
                })

            })
        },
        newQuestion () {
            this.show_question_modal = true
        },
        createQuestion (question) {
            this.axios.post('/api/unit_groups/create_question', {
                unit_group_id: this.unit_group.id,
                type: question.type,
                content: question.content,
                print_view: question.print_view,
                standards: question.standards
            })
            .then(() => {
                this.getQuestions()

                this.show_question_modal = false
            })
        },
        linkQuestion (question) {
            this.axios.post('/api/unit_groups/add_question/' + question.id, {
                unit_group_id: this.unit_group.id
            })
            .then(() => {
                this.getQuestions()
            })
        },
        uploadUnitAssessment (event) {
            let file = null
            let form_data = new FormData()

            if (
                event.target &&
                event.target.files
            ) {
                file = event.target.files[0]
                form_data.append('assessment', file)

                this.uploading_assessment = true

                this.axios.post('/api/unit_groups/add_assessment_pdf/' + this.unit_group.id, form_data)
                .then(response => {
                    if (
                        response.data &&
                        response.data.assessment_url
                    ) {
                        this.$set(this.unit_group, 'assessment_url', response.data.assessment_url)
                        this.$set(this.unit_group, 'assessment_title', response.data.assessment_title)
                    }

                    this.uploading_assessment = false
                    this.assessment_url = null
                })
                .catch(error => {
                    if (error) {
                        this.uploading_assessment = false
                        this.assessment_url = null
                    }
                })
            }
        },
        removeUnitAssessment () {
            let message = null

            if (this.unit_group.title) {
                message = 'Are you sure you want to remove the Assessment for <strong>' + this.unit_group.title + '</strong>?'
            } else {
                message = 'Are you sure you want to remove the Assessment for this unit?'
            }

            this.$swal({
                html: message,
                showCancelButton: true,
                confirmButtonText: 'Remove Assessment',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.axios.post('/api/unit_groups/remove_assessment_pdf/' + this.unit_group.id)
                    .then(() => {
                        this.$set(this.unit_group, 'assessment_url', null)
                        this.$set(this.unit_group, 'assessment_title', null)
                    })
                    .catch(() => {

                    })
                }
            })
        },
        addStandard (standard) {
            this.axios.post('/api/unit_groups/add_standard/' + this.unit_group.id, {
                standard_id: standard.guid
            })
            .then(() => {
                this.$store.dispatch('getUnitGroups', this.publication_id)
                .then(() => {
                    this.filterUnitGroupStandards(this.state_grade)
                })
            })
        },
        removeStandard (standard) {
            let standards = []

            _.forEach(this.unit_group.units, unit => {
                _.forEach(unit.standards, unit_standard => {
                    standards.push(unit_standard)
                })
            })

            if (_.find(standards, {guid: standard.guid})) {
                this.$swal({
                    html: 'This standard is linked to a week in this unit. Do you still want to remove it from this unit?',
                    showCancelButton: true,
                    confirmButtonText: 'Remove Standard',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.removeStandardFromUnitGroup(standard)
                    }
                })
            } else {
                this.removeStandardFromUnitGroup(standard)
            }

        },
        removeStandardFromUnitGroup (standard) {
            this.axios.post('/api/unit_groups/remove_standard/' + this.unit_group.id, {
                standard_id: standard.guid
            })
            .then(() => {
                this.$store.dispatch('getUnitGroups', this.publication_id)
                .then(() => {
                    this.filterUnitGroupStandards(this.state_grade)
                })
            })
        },
        newOrderNumber (unit_group_id) {
            let week_number = 1;
            _.each(this.unit_groups, unit_group => {
                _.each(unit_group.units, unit => {
                    week_number = week_number < unit.order_number ? unit.order_number : week_number;
                })
            })
            return week_number + 1;
        },
        setContent (field, value) {
            this.$set(this.unit_group, field, value)
        },
        toggleVisibility (value) {
            if (this.unit_group[value]) {
                this.$set(this.unit_group, value, false)
            } else {
                this.$set(this.unit_group, value, true)
            }
        },
        importTrunkUnit(unit_group){
            this.show_trunk_import_modal = true;
        },
        importBranchUnit(unit_group){
            this.show_trunk_import_modal = true;
        },
        getUnitGroups(){
            this.$store.dispatch('getUnitGroups', this.publication_id);
        },
        compareToTrunk(unit_group){
            this.axios.get('/api/unit_groups/compare_trunk_unit_group/' + unit_group.id +'/'+ unit_group.trunks[0].trunk_unit_group_id)
            .then(response => {
                console.log('trunk_comparison', response.data);
                this.trunk_unit_comparison = response.data;
                this.show_trunk_unit_comparison_modal = true;
            })
        },
        updateToTrunkUnit(){

        },
        refreshUnitGroup(){

        },
    },
    created () {
        if (this.unit_group) {
            this.unit_group_cache = _.cloneDeep(this.unit_group)
        }
    },
    mounted () {
        if(this.state_grade) this.filterUnitGroupStandards(this.state_grade)
        // TipTap component "beforeDestroy()" is running before unit_group component is mounted
        //this.teacher_edition_visible = false;
        this.unit_group_mounted = true
    }
}
</script>
